import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Settings {
  id: string;
  early_payment_budget: number;
  buyer_name: string;
  early_payment_days: number;
  notification_email: string;
  budget_period_start: string;
  budget_period_end: string;
  auto_generate_runs: boolean | null;
  run_generation_time: string | null;
  run_generation_timezone: string | null;
  ingest_email: string;
  // Vendor Negotiator cadence + tone settings
  negotiation_followup_days: number | null;
  negotiation_max_followups: number | null;
  negotiation_min_interval_hours: number | null;
  negotiation_decline_cooldown_days: number | null;
  cost_of_capital_apr: number | null;
  default_negotiation_tone: string | null;
  // Vendor Negotiator sender address (domain must be verified in Resend)
  negotiation_from_email: string | null;
  // Tone & Voice / global negotiation defaults (KAY-116)
  default_target_discount_pct: number | null;
  default_target_days_early: number | null;
  negotiation_signature: string | null;
}

export function useSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase.from("settings").select("*").limit(1).maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings(data as Settings);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback(
    async (updates: Partial<Omit<Settings, "id">>) => {
      if (!settings) return;

      try {
        const { error } = await supabase.from("settings").update(updates).eq("id", settings.id);

        if (error) throw error;

        setSettings((prev) => (prev ? { ...prev, ...updates } : prev));

        toast({
          title: "Settings saved",
          description: "Your settings have been updated.",
        });
      } catch (error) {
        console.error("Error updating settings:", error);
        toast({
          title: "Error",
          description: "Failed to save settings. Please try again.",
          variant: "destructive",
        });
      }
    },
    [settings, toast]
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    updateSettings,
    refetch: fetchSettings,
  };
}
